<script lang="ts" setup>
import type { Icon } from '@/components/icon/IconBasic.vue'

export interface FilterDropdownOption {
  label: string
  value: string
  icon?: Icon
}

const props = defineProps<{
  label: string
  options: FilterDropdownOption[]
  selected?: FilterDropdownOption
  icon?: Icon
}>()
const emit = defineEmits<{
  (e: 'selected', option: FilterDropdownOption): void
}>()

const isOpen = ref<boolean>(false)

function onOpen(open: boolean) {
  isOpen.value = open
}

function onSelected(value: FilterDropdownOption['value']) {
  const o = props.options.find(option => option.value === value)
  if (o)
    emit('selected', o)
}
</script>

<template>
  <div
    class="filter-dropdown"
    :class="{ 'filter-dropdown--inverted': isOpen }"
  >
    <SelectRoot
      :default-value="selected?.value"
      @update:open="onOpen"
      @update:model-value="onSelected"
    >
      <SelectTrigger
        class="filter-dropdown__trigger"
        aria-labelledby="dropdown-label"
        tabindex="0"
      >
        <SelectIcon v-if="icon">
          <IconBasic
            class="filter-dropdown__trigger__icon"
            :filename="icon"
            :color="isOpen ? '#d9d9d9' : undefined"
          />
        </SelectIcon>
        <span
          id="dropdown-label"
          class="filter-dropdown__trigger__label"
        >
          {{ label }}
        </span>
      </SelectTrigger>

      <SelectPortal>
        <SelectContent
          position="popper"
          side="bottom"
          :avoid-collisions="false"
          style="z-index: 2;"
        >
          <SelectViewport>
            <SelectItem
              v-for="(option) in options"
              :key="option.value"
              :value="option.value"
              class="filter-dropdown__option"
              :class="{ 'filter-dropdown__option--selected': option.value === selected?.value }"
              :data-test="`filter-dropdown-button-${option.value}`"
            >
              <IconBasic
                v-if="option.icon"
                :filename="option.icon"
                :style="{ width: '1.8rem', height: '1.8rem', marginRight: '0.8rem' }"
              />
              <SelectItemText>
                {{ option.label }}
              </SelectItemText>
            </SelectItem>
          </SelectViewport>
        </SelectContent>
      </SelectPortal>
    </SelectRoot>
  </div>
</template>

<style lang="scss" scoped>
@import '~/assets/scss/rules/breakpoints';
@import '~/assets/scss/typography/body';

.filter-dropdown {
  $root: &;

  &__trigger {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 1.2rem 1.6rem;
    gap: 1rem;
    border-radius: 2px;

    #{$root}--inverted & {
      background-color: black;
    }

    &__icon {
      width: 1.8rem;
      height: 1.8rem;
    }

    &__label {
      @include body2;
      line-height: unset;
      display: none;

      #{$root}--inverted & {
        color: var(--gray);
        display: block;
      }

      @media screen and (min-width: $phone) {
        display: block;
      }
    }
  }

  &__option {
    background-color: var(--gray);
    padding: 1.2rem;
    padding-left: 1.4rem;
    border-bottom: 1px solid var(--gray-dark);
    cursor: pointer;
    display: flex;
    gap: 0.4rem;
    align-items: center;
    outline-offset: -2px;

    &:hover {
      background-color: var(--yellow-hover);
    }

    &:first-child {
      border-radius: 2px 2px 0 0;
      margin-top: 4px;
    }
    &:last-child {
      border-radius: 0 0 2px 2px;
      border-bottom: none;
    }

    &--selected {
      background-color: var(--yellow);
    }

    &__icon {
      width: 1.8rem;
      height: 1.8rem;
    }
  }
}
</style>
